import {
  Component,
  ChangeDetectionStrategy,
  inject,
  input,
  output,
  signal,
  ViewChild,
  ElementRef,
  effect,
  untracked,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { ImageUrlPipe } from '../pipes/image-url.pipe';
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
} from '../confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'bn-image-upload',
  standalone: true,
  imports: [MatIconModule, MatButtonModule, ImageUrlPipe],
  templateUrl: './image-upload.component.html',
  styleUrl: './image-upload.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageUploadComponent {
  private dialog = inject(MatDialog);

  // Inputs
  currentImage = input<string | null>(null);

  constructor() {
    effect(() => {
      // Trigger on currentImage change
      this.currentImage();

      // Clear internal preview so the new external image is displayed
      // Use untracked just in case, though setting signal in effect is generally OK for this pattern if careful
      untracked(() => {
        this.internalPreviewUrl.set(null);
      });
    });
  }
  altText = input('Image');
  hint = input<string | null>(null);
  uploadLabel = input('Upload Image');

  changeLabel = input('Change Image');
  variant = input<'standard' | 'avatar'>('standard');

  // Outputs
  imageSelected = output<{ file: File; previewUrl: string }>();
  imageRemoved = output<void>();

  // Internal state
  internalPreviewUrl = signal<string | null>(null);

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const reader = new FileReader();
      reader.onload = (): void => {
        const result = reader.result as string;
        this.internalPreviewUrl.set(result);
        this.imageSelected.emit({ file, previewUrl: result });
      };
      reader.readAsDataURL(file);
    }
  }

  get displayedImage(): string | null {
    return this.internalPreviewUrl() ?? this.currentImage() ?? null;
  }

  onRemoveImage(event?: Event): void {
    event?.stopPropagation();

    const ref = this.dialog.open<ConfirmDialogComponent, ConfirmDialogData, boolean>(
      ConfirmDialogComponent,
      {
        data: {
          title: 'Remove image',
          message: 'Are you sure you want to remove this image?',
          confirmText: 'Remove',
          cancelText: 'Cancel',
        },
      },
    );

    ref.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      this.internalPreviewUrl.set(null);
      if (this.fileInput?.nativeElement) {
        this.fileInput.nativeElement.value = '';
      }
      this.imageRemoved.emit();
    });
  }

  triggerUpload(): void {
    this.fileInput.nativeElement.click();
  }
}
